<div class="prompt-research-dialog">
  <!-- Dialog Header -->
  <div class="dialog-header">
    <h2 mat-dialog-title>AI Prompt Creation</h2>
    <button mat-icon-button (click)="onClose()" class="close-button">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <mat-dialog-content class="dialog-content">
    <!-- Step 1: Form -->
    @if (currentStep === 1) {
      <div class="step-form">
        <div class="question-section">
          <h3>How would you like to get started?</h3>
        </div>

        <!-- Research Type Selection -->
        <mat-radio-group [(ngModel)]="researchType" class="research-type-group">
          <mat-radio-button value="seo_keywords" class="research-option">
            <div class="option-content">
              <strong>I have SEO keywords and want help turning them into Search Prompts</strong>
            </div>
          </mat-radio-button>

          <mat-radio-button value="url_analysis" class="research-option">
            <div class="option-content">
              <strong>I have a specific URL and want to discover which prompts are driving traffic</strong>
            </div>
          </mat-radio-button>

          <mat-radio-button value="brand_brainstorm" class="research-option">
            <div class="option-content">
              <strong>I want to brainstorm new Search Prompts based on my brand</strong>
            </div>
          </mat-radio-button>
        </mat-radio-group>

        <!-- SEO Keywords Input -->
        @if (researchType === 'seo_keywords') {
          <div class="form-section">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>SEO Keywords</mat-label>
              <textarea matInput 
                        [(ngModel)]="keywords" 
                        placeholder="Enter your SEO keywords here (one keyword per line)"
                        rows="8"
                        [maxlength]="2000"></textarea>
              <mat-hint>{{ getKeywordCount() }}/20 keywords</mat-hint>
            </mat-form-field>
          </div>
        }

        <!-- URL Input -->
        @if (researchType === 'url_analysis') {
          <div class="form-section">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>URL</mat-label>
              <input matInput 
                     [(ngModel)]="url" 
                     placeholder="https://example.com/page"
                     type="url">
            </mat-form-field>
          </div>
        }

        <!-- Brand Brainstorm Inputs -->
        @if (researchType === 'brand_brainstorm') {
          <div class="form-section">
            <mat-form-field appearance="outline" class="full-width form-field">
              <mat-label>Brand Name</mat-label>
              <input matInput [(ngModel)]="brandInfo.brand_name" placeholder="Enter brand name" required>
            </mat-form-field>

            <mat-form-field appearance="outline" class="full-width form-field">
              <mat-label>Industry</mat-label>
              <input matInput [(ngModel)]="brandInfo.industry" placeholder="e.g., Technology, Healthcare">
            </mat-form-field>

            <mat-form-field appearance="outline" class="full-width form-field">
              <mat-label>Products/Services</mat-label>
              <input matInput [(ngModel)]="brandInfo.products" placeholder="Comma-separated list">
              <mat-hint>Separate multiple products/services with commas</mat-hint>
            </mat-form-field>

            <mat-form-field appearance="outline" class="full-width form-field">
              <mat-label>Target Audience</mat-label>
              <input matInput [(ngModel)]="brandInfo.target_audience" placeholder="e.g., Small businesses, Students">
            </mat-form-field>
          </div>
        }

        <!-- Language and Country Selection -->
        <div class="form-section form-row">
          <mat-form-field appearance="outline">
            <mat-label>Language</mat-label>
            <mat-select [(ngModel)]="language">
              @for (opt of languageOptions; track opt.code) {
                <mat-option [value]="opt.label">{{ opt.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Country/Market</mat-label>
            <mat-select [(ngModel)]="country">
              @for (country of countryOptions; track country) {
                <mat-option [value]="country">{{ country }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        </div>
      </div>
    }

    <!-- Step 2: Results -->
    @if (currentStep === 2) {
      <div class="step-results">
        @if (loading) {
          <div class="loading-container">
            <mat-spinner diameter="50"></mat-spinner>
            <p>Loading research...</p>
          </div>
        } @else {
          <div class="results-header">
            <h3>Generated Prompts <span class="results-count">({{ prompts.length }} prompts generated):</span></h3>
            <!-- <p class="results-count">{{ prompts.length }} prompts generated</p> -->
          </div>

          <div class="prompts-list">
            @if (prompts.length === 0) {
              <div class="empty-results">
                <mat-icon>info_outline</mat-icon>
                <p>No prompts were generated. Please try again with different inputs.</p>
              </div>
            } @else {
              <div class="prompts-container">
                <div class="prompts-table">
                  <!-- Table Header -->
                  <div class="prompts-header">
                    <div class="header-cell prompt-number-header">#</div>
                    <div class="header-cell prompt-text-header">Prompt</div>
                    <div class="header-cell prompt-metric-header">Relevance Score</div>
                    <div class="header-cell prompt-metric-header">Monthly AI Intent Volume</div>
                  </div>
                  <!-- Table Body -->
                  <div class="prompts-body">
                    @for (prompt of prompts; track $index) {
                      <div class="prompt-row">
                        <div class="cell prompt-number">{{ $index + 1 }}</div>
                        <div class="cell prompt-text">{{ prompt.text }}</div>
                        <div class="cell prompt-metric">
                          <span class="relevance-score" 
                                [class.high-relevance]="isHighRelevanceScore(prompt.relevance_score)">
                            {{ prompt.relevance_score | number:'1.1-1' }}
                          </span>
                        </div>
                        <div class="cell prompt-metric">
                          <span class="volume-badge" 
                                [style.background]="getVolumeGradient(prompt.monthly_intent_volume)">
                            {{ prompt.monthly_intent_volume | number }}
                          </span>
                        </div>
                      </div>
                    }
                  </div>
                </div>
              </div>
            }
          </div>
        }
      </div>
    }
  </mat-dialog-content>

  <mat-dialog-actions class="dialog-actions">
    @if (currentStep === 1) {
      <button mat-button (click)="onClose()">Cancel</button>
      <button mat-raised-button 
              color="primary" 
              (click)="onNextStep()" 
              [disabled]="generating">
        @if (generating) {
          <mat-spinner diameter="20" class="button-spinner"></mat-spinner>
          Generating...
        } @else {
          Next step
        }
      </button>
    } @else {
      <button mat-button (click)="onClose()">Close</button>
      @if (prompts.length > 0) {
        <button mat-button (click)="copyAllPrompts()">
          <mat-icon>content_copy</mat-icon>
          Copy All
        </button>
        <button mat-raised-button color="primary" (click)="exportToCSV()">
          <mat-icon>download</mat-icon>
          Export to CSV
        </button>
      }
    }
  </mat-dialog-actions>
</div>
